import React, { useCallback, useMemo, useState } from 'react';
import { StyledAccordion } from './styles';

export type AccordionSectionPublicProps = {
    title?: string;
    open?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    onToggle?: () => void;
};

type SectionLike = React.ReactElement<
    AccordionSectionPublicProps & { index?: number; toggleOpened?: (p?: any) => void }
>;

export type AccordionProps = {
    children: SectionLike | SectionLike[];
    allowMultipleOpened?: boolean;
    onChange?: (openStates: boolean[]) => void;
    className?: string;
} & React.HTMLAttributes<HTMLDivElement>;

const Accordion: React.FC<AccordionProps> = ({ children, allowMultipleOpened = true, onChange, ...otherProps }) => {
    const list = useMemo(() => React.Children.toArray(children) as SectionLike[], [children]);

    const [sectionState, setSectionState] = useState<boolean[]>(list.length > 0 ? list.map(c => !!c.props.open) : []);

    const toggleOpened = useCallback(
        (index: number) => {
            let next = [...sectionState];
            if (!allowMultipleOpened) {
                next = next.map((val, i) => (i === index ? val === false : false));
            } else {
                next[index] = !next[index];
            }
            setSectionState(next);
            onChange?.(next);
        },
        [allowMultipleOpened, onChange, sectionState]
    );

    if (list.length === 1) {
        const only = list[0];
        return React.cloneElement(only, {
            index: 0,
            open: sectionState[0],
            toggleOpened: () => toggleOpened(0),
        });
    }

    return (
        <StyledAccordion {...otherProps}>
            {list.map((child, index) =>
                React.cloneElement(child, {
                    index,
                    open: sectionState[index],
                    toggleOpened: () => toggleOpened(index),
                })
            )}
        </StyledAccordion>
    );
};

export default Accordion;
